import cjaAnalytics from 'yoda-core-components/lib/helpers/Analytics/CJAAnalytics';
import { triggerAddSaveItems } from 'yoda-core-components/lib/helpers/Utils/Utils';
import buildCJAPageViewPayload from 'yoda-core-components/lib/helpers/Analytics/CJAPageViewBuilder';
import buildCJAEvent from 'yoda-core-components/lib/helpers/Analytics/CJAEventBuilder';
import { ADD_SAVEDITEMS } from 'yoda-site-components/lib/actionTypes/SavedItemActionTypes';
import {
    BOPIS_DEFAULT_STORE_CHANGE,
    DYNAMIC_GALLERIES_ITEM_CLICK,
    DYNAMIC_GALLERIES_MODULE_CLICK,
    DYNAMIC_VISNAV_CLICK,
    GALLERY_VIDEO_PLAYER_ANALYTICS,
    ON_PAGE_READY,
    PRODUCT_FILTERS_APPLIED,
    SEARCH_RESULT_CLICK,
    TRIGGER_STORE_SEARCH_EVENT,
} from '../actionTypes/AnalyticsActionTypes';
import { UPDATE_VIEW_TYPE } from '../actionTypes/ProductListActionTypes';
import { BOPIS_GALLERY_CHANGE_ZIP } from '../actionTypes/BOPISActionTypes';
import constants from '../analytics/Analytics.config';
import { getCategoryObj, setsbResultsInfo } from '../analytics/analytics.helper';
import {
    getPageType,
    getURLParameterBykey,
    getLastFilter,
    isFilterAnalytics,
    feedPageLoad,
} from '../utils';
import getBreadCrumbInfo from '../selectors/BreadCrumbs';

const buildPageViewPayload = (state) => {
    const context = state.context || {};
    const pagename = getPageType();
    const isGalleryPage = pagename === 'g';
    const nullPage = isGalleryPage ? constants.PAGE_ng : constants.PAGE_ns;
    const pageType = state.countOfSearchResults ? constants[`PAGE_${pagename}`] : nullPage;
    const categoryObj = getCategoryObj(state, pageType);

    const breadcrumbs = getBreadCrumbInfo(state);
    const primary = categoryObj.primaryCategory || pageType;
    const sub1 = categoryObj.subCategory1 || '';
    const sub2 = categoryObj.subCategory2 || '';
    const breadcrumbPath = (breadcrumbs || [])
        .map((b) => b.breadCrumbLabel || '')
        .filter(Boolean)
        .join('>')
        .toLowerCase();

    return buildCJAPageViewPayload(
        {
            name: `d:${pageType}:${primary}`.toLowerCase(),
            type: pageType,
            breadcrumbs: breadcrumbPath,
            siteSection: primary,
            category: {
                primary,
                sub1,
                sub2,
            },
            flags: {
                isErrorPage: !!state.ErrorPage,
                isSponsorAdsDisplayed: !!(state.sponsoredDisplayPage && state.products?.length > 0),
            },
            tech: {
                releaseVersion: context.__RELEASEVERSION__ || '',
                // eslint-disable-next-line no-nested-ternary
                version: state.countOfSearchResults
                    ? `apollo 1 ${pageType}`
                    : isGalleryPage
                    ? constants.VERSION_ng
                    : constants.VERSION_ns,
            },
            sbResultsInfo: setsbResultsInfo(state),
        },
        {},
        {
            interactionName: isGalleryPage ? 'product_list_view' : 'view_search_results',
        }
    );
};

const CJAMiddleware = (store) => (next) => (action) => {
    if (typeof window === 'undefined') return next(action);
    if (!cjaAnalytics.isEnabled()) return next(action);

    const state = store.getState();
    let eventName = null;
    let eventData = null;

    switch (action.type) {
        case ON_PAGE_READY: {
            eventName = 'pageView';
            eventData = buildPageViewPayload(state);

            if (
                getURLParameterBykey('searchType')?.match(/predictive/i) &&
                getURLParameterBykey('brand')
            ) {
                cjaAnalytics.track(
                    'linkClick',
                    buildCJAEvent({
                        event: 'linkClick',
                        event_name: 'linkClick',
                        linkRegion: 'gallery',
                        linkComponent: 'predictive search with brand filter',
                        linkId: 'event364',
                        linkModuleName: 'predictive search with brand filter',
                    })
                );
            }
            break;
        }
        case ADD_SAVEDITEMS: {
            if (triggerAddSaveItems(state)) {
                const product = action.payload?.[0]?.product || {};
                eventName = 'save_product';
                eventData = {
                    event_name: 'save_product',
                    products: [
                        {
                            id: product.id || '',
                            name: product.productName || '',
                            link: product.productLink || '',
                            imgSource: product.productImageSrc || '',
                        },
                    ],
                };
            }
            break;
        }
        case SEARCH_RESULT_CLICK: {
            const { sbResultsInfo = {} } = action.payload || {};
            const productInfo = sbResultsInfo?.product?.[0]?.productInfo || {};
            const ppId = productInfo.productPPID || '';
            eventData = {
                event: 'search_results_click',
                event_name: 'search_results_click',
                searchTerm: getURLParameterBykey('Ntt') || getURLParameterBykey('searchTerm') || '',
                searchType: getURLParameterBykey('searchType') || '',
                searchLinkText: productInfo.productName || ppId,
                searchLinkHref: productInfo.productLink || '',
                searchClickPosition: parseInt(sbResultsInfo.resultPosition, 10) || 0,
                searchLinkId: ppId,
            };
            break;
        }
        case DYNAMIC_GALLERIES_ITEM_CLICK: {
            const { dynamicGalleryID = '', dynamicGalleryPPID = '' } = action.payload || {};
            eventData = buildCJAEvent({
                event: 'linkClick',
                event_name: 'linkClick',
                linkRegion: ' product grid zone',
                linkComponent: 'dynamic gallery plp',
                linkModuleName: `dynamicGallery:${dynamicGalleryID}:${dynamicGalleryPPID}`,
            });
            break;
        }
        case DYNAMIC_GALLERIES_MODULE_CLICK: {
            const { dynamicGalleryID = '' } = action.payload || {};
            eventData = buildCJAEvent({
                event: 'linkClick',
                event_name: 'linkClick',
                linkRegion: 'product grid zone',
                linkComponent: 'dynamic gallery plp',
                linkModuleName: `dynamicGallery:${dynamicGalleryID}`,
            });
            break;
        }
        case DYNAMIC_VISNAV_CLICK: {
            const { dynamicVisualMapPositionClicked = '' } = action.payload || {};
            eventData = buildCJAEvent({
                event: 'linkClick',
                event_name: 'linkClick',
                linkRegion: 'gallery',
                linkComponent: 'dynamic vis nav',
                linkId: dynamicVisualMapPositionClicked,
                linkText: dynamicVisualMapPositionClicked,
                linkModuleName: 'dynamicVisNav',
            });
            break;
        }
        case PRODUCT_FILTERS_APPLIED: {
            // TODO: linkClick field values TBD — pending final spec from Jason
            let lastFilter = getLastFilter();
            const promotedFilter = state.promotedFilterSelected || {};
            if (promotedFilter.value === 'interactivevisnav') {
                lastFilter = {
                    action: 'add',
                    name: 'interactivevisnav',
                    value: promotedFilter.heading,
                };
            } else if (promotedFilter.value === 'suggestedfilter') {
                lastFilter = {
                    action: 'add',
                    name: 'suggestedfilter',
                    value: promotedFilter.heading,
                };
            }
            const selectedFilters = state.selectedFilters?.length || 0;
            if ((selectedFilters || lastFilter) && isFilterAnalytics()) {
                const filterName = lastFilter?.name || '';
                const filterValue = lastFilter?.value || '';
                const filterAction = lastFilter?.action || '';
                eventData = buildCJAEvent({
                    event: 'linkClick',
                    event_name: 'linkClick',
                    linkRegion: 'filter',
                    linkComponent: 'product filters applied',
                    linkId: `${filterName}:${filterValue}`,
                    linkText: `${filterAction}:${filterName}:${filterValue}`,
                    linkModuleName: 'productFiltersApplied',
                });
                feedPageLoad();
            }
            break;
        }
        case BOPIS_GALLERY_CHANGE_ZIP:
            eventData = buildCJAEvent({
                event: 'linkClick',
                event_name: 'linkClick',
                linkRegion: 'gallery',
                linkComponent: 'bopis change zip',
                linkId: 'bopisChangeZip',
                linkText: 'change zip',
                linkModuleName: 'bopisChangeZip',
            });
            break;
        case UPDATE_VIEW_TYPE: {
            if (action.payload?.userAction) {
                const viewType = (action.payload.viewType || '').toLowerCase();
                eventData = buildCJAEvent({
                    event: 'linkClick',
                    event_name: 'linkClick',
                    linkRegion: 'gallery',
                    linkComponent: 'switch page orientation',
                    linkId: viewType,
                    linkText: viewType,
                    linkModuleName: `switchPageOrientation:${viewType}`,
                });
            }
            break;
        }
        case GALLERY_VIDEO_PLAYER_ANALYTICS: {
            const { videoPlayerData, product } = action.payload || {};
            const ppId = product?.[0]?.productInfo?.productPPID || '';
            eventName = 'impressions';
            eventData = {
                event_name: 'impressions',
                impressionId: ppId,
                impressionName: videoPlayerData?.id?.() || '',
                impressionType: 'video interaction',
            };
            break;
        }
        case TRIGGER_STORE_SEARCH_EVENT: {
            const { searchText = '' } = action;
            eventData = buildCJAEvent({
                event: 'linkClick',
                event_name: 'linkClick',
                linkRegion: 'gallery',
                linkComponent: 'store search',
                linkId: 'storeSearch',
                linkText: searchText,
                linkModuleName: `storeSearch:${searchText}`,
            });
            break;
        }
        case BOPIS_DEFAULT_STORE_CHANGE:
            eventData = buildCJAEvent({
                event: 'linkClick',
                event_name: 'linkClick',
                linkRegion: 'gallery',
                linkComponent: 'bopis default store change',
                linkId: 'bopisDefaultStoreChange',
                linkModuleName: 'bopisDefaultStoreChange',
            });
            break;
        default:
            break;
    }

    if (eventData) {
        cjaAnalytics.track(eventName || eventData.event, eventData);
    }

    return next(action);
};

export default CJAMiddleware;
